import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import ConfirmationMessage from '@/common/components/blocks/Modal/Common/ConfirmationMessage/ConfirmationMessage';
import { ENDPOINT_PRODUCTS_QUICK_ENTRY, SOURCE } from '@/common/constants';
import { useAddToContext } from '@/common/contexts/AddToContext';
import { useAuth } from '@/common/contexts/AuthContext';
import { useModalContext } from '@/common/contexts/ModalContext';
import SessionStorageScanbat from '@/common/services/SessionStorage.scanbat';
import Scandit from '@/ebat/components/blocks/Modal/Common/Scandit/Scandit';
import { useScanbatSessionContext } from '@/ebat/contexts/ScanbatSessionContext/ScanbatSessionContext';
import useUpdateQuickEntry from '@/ebat/mutations/products/quick-entry/useUpdateQuickEntry';

const useScanbat = ({
  disabled,
  isLoading,
  localCart,
  refetchCart,
  warehouse,
}) => {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const {
    setModalClassName,
    setModalContent,
    setModalIsOpen,
  } = useModalContext();
  const { addToCart } = useAddToContext();
  const { isSessionEnded } = useScanbatSessionContext();

  const {
    mutate: mutateUpdateQuickEntry,
    isLoading: isLoadingUpdateQuickEntry,
  } = useUpdateQuickEntry([ENDPOINT_PRODUCTS_QUICK_ENTRY]);
  const handleUpdateQuickEntry = useCallback(
    (term, onError, onSuccess) =>
      mutateUpdateQuickEntry({ term }, { onError, onSuccess }),
    [mutateUpdateQuickEntry],
  );

  const scanbatService = SessionStorageScanbat.getService();

  const isDisabled = useMemo(() => {
    const loading = isLoading || isLoadingUpdateQuickEntry;

    return {
      loading,
      disabled: loading || disabled,
    };
  }, [disabled, isLoading, isLoadingUpdateQuickEntry]);

  const onRemoveWarehouse = useCallback(() => {
    setModalContent(
      <ConfirmationMessage
        description={t('ebat:messages:actions.warehouse.update.description', {
          warehouse: warehouse.name,
        })}
        labelCancel={t('common:messages:actions.cancel')}
        labelSubmit={t('common:messages:actions.logout')}
        onSubmit={() => {
          scanbatService.clearScanBat();
          logout();
        }}
        title={t('ebat:messages:actions.warehouse.update.title')}
      />,
    );
    setModalClassName('modal-confirmation-message');
    setModalIsOpen(true);
  }, [
    logout,
    scanbatService,
    setModalClassName,
    setModalContent,
    setModalIsOpen,
    t,
    warehouse,
  ]);

  const onScanResult = useCallback(
    (scanResult) => {
      if (scanResult.barcodes.length > 0) {
        scanResult.barcodes.forEach((barcode) =>
          handleUpdateQuickEntry(barcode.data, null, (items) =>
            addToCart({
              items: [
                {
                  brand: items[0].brand,
                  name: items[0].name,
                  quantity:
                    items[0].sales_packaging && !items[0].split_packaging
                      ? items[0].sales_packaging
                      : 1,
                  sku: items[0].sku || items[0].main_sku,
                },
              ],
              cart: localCart,
              onSuccessCallback: () => {
                refetchCart();
              },
            }),
          ),
        );
      }
    },
    [addToCart, handleUpdateQuickEntry, localCart, refetchCart],
  );

  const onAddTocart = useCallback(
    (item) => {
      addToCart({
        items: [
          {
            brand: item[0].brand,
            name: item[0].name,
            quantity: item[0].quantity,
            sku: item[0].sku || item[0].main_sku,
            source: SOURCE.SCANBAT,
          },
        ],
        cart: localCart,
        onSuccessCallback: () => {
          refetchCart();
        },
        options: {
          linkAction: () => {
            setModalIsOpen(false);
          },
        },
      });
    },
    [addToCart, localCart, refetchCart, setModalIsOpen],
  );

  useEffect(() => {
    if (isSessionEnded) {
      setModalIsOpen(false);
    }
  }, [isSessionEnded, setModalIsOpen]);
  const onScanBarCode = useCallback(() => {
    setModalContent(
      <Scandit
        onScanResult={onScanResult}
        onValidatePopin={onAddTocart}
        handleUpdateQuickEntry={handleUpdateQuickEntry}
      />,
    );
    setModalClassName('modal-scandit');
    setModalIsOpen(true);
  }, [
    handleUpdateQuickEntry,
    onAddTocart,
    onScanResult,
    setModalClassName,
    setModalContent,
    setModalIsOpen,
  ]);

  return {
    isDisabled,
    onAddTocart,
    onRemoveWarehouse,
    onScanBarCode,
    handleUpdateQuickEntry,
  };
};

export default useScanbat;
